import { useState, useEffect, useCallback, useRef } from 'react';
import { THEMES, DEFAULT_THEME_ID } from '@/utils/themes';
import { api } from '@/utils/api';

const STORAGE_KEY = 'vc_theme';
const STYLE_ID = 'vc-theme-override';

const SAGE_SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

const SCOPE = '[data-theme-scope="dashboard"]';

function buildThemeCSS(theme) {
  const varDecls = Object.entries(theme.cssVars)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n');

  const sageOverrides = SAGE_SHADES.flatMap((shade) => {
    const color = theme.sage[shade];
    if (!color) return [];
    return [
      `${SCOPE} .bg-sage-${shade} { background-color: ${color} !important; }`,
      `${SCOPE} .text-sage-${shade} { color: ${color} !important; }`,
      `${SCOPE} .border-sage-${shade} { border-color: ${color} !important; }`,
      `${SCOPE} .hover\\:bg-sage-${shade}:hover { background-color: ${color} !important; }`,
      `${SCOPE} .hover\\:text-sage-${shade}:hover { color: ${color} !important; }`,
      `${SCOPE} .focus\\:border-sage-${shade}:focus { border-color: ${color} !important; }`,
      `${SCOPE} .ring-sage-${shade} { --tw-ring-color: ${color} !important; }`,
      `${SCOPE} .from-sage-${shade} { --tw-gradient-from: ${color} var(--tw-gradient-from-position) !important; }`,
      `${SCOPE} .to-sage-${shade} { --tw-gradient-to: ${color} var(--tw-gradient-to-position) !important; }`,
      `${SCOPE} .via-sage-${shade} { --tw-gradient-via: ${color} var(--tw-gradient-via-position) !important; }`,
    ];
  }).join('\n');

  // For midnight (dark) theme, also force card and popover backgrounds
  const darkOverrides = theme.id === 'midnight' ? `
${SCOPE} .bg-white { background-color: hsl(var(--card)) !important; }
${SCOPE} .text-gray-800 { color: hsl(var(--foreground)) !important; }
${SCOPE} .text-gray-700 { color: hsl(215 20% 80%) !important; }
${SCOPE} .text-gray-600 { color: hsl(215 20% 70%) !important; }
${SCOPE} .text-gray-500 { color: hsl(215 20% 65%) !important; }
${SCOPE} .text-gray-400 { color: hsl(215 20% 55%) !important; }
${SCOPE} .border-stone-100, ${SCOPE} .border-stone-200 { border-color: hsl(var(--border)) !important; }
${SCOPE} .bg-stone-50, ${SCOPE} .bg-stone-100 { background-color: hsl(217 33% 22%) !important; }
` : '';

  // CSS custom properties and background scoped to the dashboard wrapper only
  return `${SCOPE} {
${varDecls}
  background-color: ${theme.bgColor};
}

${sageOverrides}
${darkOverrides}`;
}

function applyThemeToDOM(theme) {
  let el = document.getElementById(STYLE_ID);
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent = buildThemeCSS(theme);
}

export function useTheme() {
  const [themeId, setThemeId] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_THEME_ID;
  });

  // Track whether we've already loaded the server preference this session
  const loadedFromServer = useRef(false);

  const theme = THEMES.find((t) => t.id === themeId) || THEMES[0];

  // Apply CSS whenever theme changes
  useEffect(() => {
    applyThemeToDOM(theme);
  }, [theme]);

  // On mount: if authenticated, pull the saved preference from the server
  useEffect(() => {
    if (loadedFromServer.current) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    loadedFromServer.current = true;
    api.get('/users/me').then((res) => {
      const serverTheme = res.data?.preferences?.theme;
      if (serverTheme && THEMES.find((t) => t.id === serverTheme)) {
        localStorage.setItem(STORAGE_KEY, serverTheme);
        setThemeId(serverTheme);
      }
    }).catch(() => {
      // Network / auth error — keep the locally stored value
    });
  }, []);

  const setTheme = useCallback((id) => {
    localStorage.setItem(STORAGE_KEY, id);
    setThemeId(id);

    // Persist to the server if authenticated (fire-and-forget)
    const token = localStorage.getItem('token');
    if (token) {
      api.patch('/users/me/preferences', { preferences: { theme: id } }).catch(() => {});
    }
  }, []);

  return { themeId, theme, setTheme, themes: THEMES };
}
